import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useCampaigns } from '../lib/api/campaigns';
import { useDashboard, useAlerts, DashboardSocialItem } from '../lib/api/dashboard';
import { useBrands } from '../lib/api/brands';
import { GanttTimeline } from '../components/gantt/GanttTimeline';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { Sparkline } from '../components/ui/Sparkline';
import { Chip } from '../components/ui/Chip';
import { fmtShort, today, toISODate, fmtRelative } from '../lib/dates';
import CreateCampaignModal from '../components/modals/CreateCampaignModal';
import CreateBrandModal from '../components/modals/CreateBrandModal';
import { TaskName } from '../components/tasks/TaskName';

const typeColor = (t: string) =>
  t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: campaigns = [] } = useCampaigns();
  const { data: dashboard } = useDashboard();
  const { data: alerts = [] } = useAlerts();
  const { data: brands = [] } = useBrands();
  const brandById = new Map(brands.map((b) => [b.id, b]));
  const campaignById = new Map(campaigns.map((c) => [c.id, c]));

  const [campaignOpen, setCampaignOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  const todayDate = today();
  const todayLabel = fmtShort(todayDate);

  const weekDays = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(todayDate);
      d.setDate(todayDate.getDate() + i);
      out.push(d);
    }
    return out;
  }, [todayDate]);

  const postsByDate = useMemo(() => {
    const m = new Map<string, DashboardSocialItem[]>();
    if (!dashboard) return m;
    for (const p of dashboard.social_preview) {
      const arr = m.get(p.publish_date) ?? [];
      arr.push(p);
      m.set(p.publish_date, arr);
    }
    return m;
  }, [dashboard]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Marketing ops — {todayLabel}</div>
          <h1 className="page-title">
            {user?.displayName ? `Hey ${user.displayName},` : 'Hello,'} here's what's shipping.
          </h1>
          <p className="page-sub">
            {dashboard?.active_campaigns ?? 0} active, {dashboard?.planned_campaigns ?? 0} planned campaigns.{' '}
            {dashboard?.tasks_this_week ?? 0} open tasks this week,{' '}
            {dashboard?.posts_next_7d ?? 0} posts on deck, {dashboard?.alerts_count ?? 0} overlap alerts.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={() => setBrandOpen(true)}>
            <Icon name="plus" size={13} /> New brand
          </Button>
          <Button variant="primary" onClick={() => setCampaignOpen(true)}>
            <Icon name="plus" size={13} /> New campaign
          </Button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-label">Active campaigns</div>
          <div className="kpi-value">{dashboard?.active_campaigns ?? 0}</div>
          <div className="kpi-delta">
            <Sparkline values={[2, 3, 3, 2, 4, 3, dashboard?.active_campaigns ?? 0]} color="var(--accent-4)" />
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Tasks this week</div>
          <div className="kpi-value">{dashboard?.tasks_this_week ?? 0}</div>
          <div className="kpi-delta">
            <Sparkline values={[3, 4, 5, 4, 6, 5, dashboard?.tasks_this_week ?? 0]} color="var(--accent-1)" />
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Posts next 7d</div>
          <div className="kpi-value">{dashboard?.posts_next_7d ?? 0}</div>
          <div className="kpi-delta">
            <Sparkline values={[1, 2, 3, 3, 4, 5, dashboard?.posts_next_7d ?? 0]} color="var(--accent-5)" />
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Overlap alerts</div>
          <div className="kpi-value" style={{ color: alerts.length > 0 ? 'var(--warn)' : undefined }}>
            {alerts.length}
          </div>
          <div className="kpi-delta">
            {alerts.length === 0 ? 'All clear' : 'Review below'}
          </div>
        </div>
      </div>

      <div className="section" style={{ marginTop: 8 }}>
        <div className="section-head">
          <div>
            <div className="section-title">Timeline</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
              12 weeks centered on today
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 11.5, color: 'var(--ink-4)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--sitewide)' }} /> Sitewide
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--brand)' }} /> Brand
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--seasonal)' }} /> Seasonal
            </span>
          </div>
        </div>
        {campaigns.length === 0 ? (
          <div className="card" style={{ padding: 32, color: 'var(--ink-4)', textAlign: 'center' }}>
            No campaigns yet. Create one to populate the timeline.
          </div>
        ) : (
          <GanttTimeline
            campaigns={campaigns}
            onCampaignClick={(id) => navigate(`/campaigns/${id}`)}
          />
        )}
      </div>

      <div className="grid grid-dash" style={{ marginTop: 32 }}>
        <div>
          <div className="section-head">
            <div>
              <div className="section-title">Upcoming tasks</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                Next 14 days
              </div>
            </div>
            <button type="button" className="section-action" onClick={() => navigate('/tasks')}>
              View all →
            </button>
          </div>
          <div className="card">
            {(dashboard?.upcoming_tasks ?? []).length === 0 ? (
              <div style={{ padding: 24, color: 'var(--ink-4)' }}>No upcoming tasks.</div>
            ) : (
              (dashboard?.upcoming_tasks ?? []).map((t) => {
                const c = t.campaign ? campaignById.get(t.campaign) : undefined;
                const clickable = !!t.campaign;
                return (
                  <div
                    key={t.id}
                    className="row"
                    style={{
                      gridTemplateColumns: '1fr 100px 90px',
                      cursor: clickable ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (t.campaign) navigate(`/campaigns/${t.campaign}`);
                    }}
                  >
                    <div>
                      <div className="row-title"><TaskName name={t.name} /></div>
                      <div className="row-meta">
                        <span
                          style={{
                            display: 'inline-block',
                            width: 6,
                            height: 6,
                            borderRadius: 50,
                            background: c ? typeColor(c.type) : 'var(--ink-5)',
                            marginRight: 5,
                          }}
                        />
                        {c?.name ?? (t.campaign ?? 'Standalone')}
                      </div>
                    </div>
                    <Chip kind="status" value={t.status} />
                    <div className="mono" style={{ fontSize: 11.5, textAlign: 'right', color: 'var(--ink-3)' }}>
                      {fmtRelative(t.due_date)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div>
          <div className="section-head">
            <div>
              <div className="section-title">Conflict alerts</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {alerts.length} detected
              </div>
            </div>
          </div>
          <div className="card">
            {alerts.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--ink-4)', textAlign: 'center' }}>
                No overlaps detected.
              </div>
            ) : (
              alerts.map((a, i) => (
                <div key={i} className="alert-item">
                  <div className="alert-icon">
                    <Icon name="alert" size={13} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{a.message}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 6 }}>{a.detail}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {a.brand_id && brandById.get(a.brand_id) && (
                        <span className="chip neutral">
                          {brandById.get(a.brand_id)!.name}
                        </span>
                      )}
                      {a.campaign_ids.map((cid) => (
                        <button
                          key={cid}
                          type="button"
                          className="chip warn"
                          style={{ cursor: 'pointer', border: 'none' }}
                          onClick={() => navigate(`/campaigns/${cid}`)}
                        >
                          {campaignById.get(cid)?.name ?? cid}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <div className="section-title">Social — next 7 days</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
              {dashboard?.social_preview.length ?? 0} post{dashboard?.social_preview.length === 1 ? '' : 's'}
            </div>
          </div>
          <button type="button" className="section-action" onClick={() => navigate('/social')}>
            Open planner →
          </button>
        </div>
        <div className="cal" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {weekDays.map((d) => {
            const iso = toISODate(d);
            const dayPosts = postsByDate.get(iso) ?? [];
            const isToday = toISODate(todayDate) === iso;
            return (
              <div
                key={iso}
                className={`cal-cell ${isToday ? 'today' : ''}`}
                style={{ minHeight: 110 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="cal-date">{d.getDate()}</div>
                  <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {d.toLocaleDateString('en', { weekday: 'short' })}
                  </div>
                </div>
                {dayPosts.map((p) => {
                  const c = p.campaign ? campaignById.get(p.campaign) : null;
                  return (
                    <div
                      key={p.id}
                      className="cal-post"
                      style={{ borderLeftColor: c ? typeColor(c.type) : 'var(--ink-5)' }}
                      onClick={() => c && navigate(`/campaigns/${c.id}`)}
                    >
                      <Icon name={p.platform === 'Instagram' ? 'instagram' : 'facebook'} size={11} />
                      {p.publish_time && <span className="cal-post-time">{p.publish_time}</span>}
                      <span className="cal-post-caption">{p.title || p.caption}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <CreateCampaignModal
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        onCreated={(id) => navigate(`/campaigns/${id}`)}
      />
      <CreateBrandModal
        open={brandOpen}
        onClose={() => setBrandOpen(false)}
        onCreated={(id) => navigate(`/brands/${id}`)}
      />
    </div>
  );
};

export default Dashboard;
